import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONCONTRACT_LOAD_BY_INSTITUTION_ID,
    ACTION_INSTITUTIONCONTRACT_LOAD_BY_ID,
    ACTION_INSTITUTIONCONTRACT_LOAD_FAILURE,
    ACTION_INSTITUTIONCONTRACT_LOAD_SUCCESS,

    ACTION_INSTITUTIONCONTRACT_SAVE,
    ACTION_INSTITUTIONCONTRACT_SAVE_SUCCESS,
    ACTION_INSTITUTIONCONTRACT_SAVE_FAILURE,

    ACTION_INSTITUTIONCONTRACT_VALIDATE,
    ACTION_INSTITUTIONCONTRACT_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONCONTRACT_VALIDATE_FAILURE,

    ACTION_INSTITUTIONCONTRACT_CREATE,
    ACTION_INSTITUTIONCONTRACT_CREATE_SUCCESS,
    ACTION_INSTITUTIONCONTRACT_CREATE_FAILURE,

    ACTION_INSTITUTIONCONTRACT_SYNC,
    ACTION_INSTITUTIONCONTRACT_SYNC_SUCCESS,
    ACTION_INSTITUTIONCONTRACT_SYNC_FAILURE,

    ACTION_INSTITUTIONCONTRACT_CLEAR,
    ACTION_INSTITUTIONCONTRACT_FILTER,

    InstitutionContractAction
} from '../actions/institutionContract';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionContractState {
    formData: Dtos.InstitutionContract | Dtos.InstitutionContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionContract[];
    formLength: number;
}

const initialState: IInstitutionContractState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionContractState, _isLoading: boolean): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IInstitutionContractState,
    formData: Dtos.InstitutionContract | Dtos.InstitutionContract[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IInstitutionContractState, responseStatus: Dtos.ResponseStatus): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IInstitutionContractState, formFilter: IFormFilter): IInstitutionContractState {

    const items: Dtos.InstitutionContract[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.agreementName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: IInstitutionContractState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IInstitutionContractState, propertiesToClear?: string[]): IInstitutionContractState {

    if (!propertiesToClear) {
        const newState: IInstitutionContractState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionContractState, _isSaving: boolean): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionContractState,
    formData: Dtos.InstitutionContract | Dtos.InstitutionContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionContractState, responseStatus: Dtos.ResponseStatus): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IInstitutionContractState, _isValidating: boolean): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionContractState,
    formData: Dtos.InstitutionContract | Dtos.InstitutionContract[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionContractState, responseStatus: Dtos.ResponseStatus): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionContractState, _isCreating: boolean): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionContractState,
    formData: Dtos.InstitutionContract,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionContractState, responseStatus: Dtos.ResponseStatus): IInstitutionContractState {
    const newState: IInstitutionContractState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus.errorCode,
                errorMessage: responseStatus.message,
                errors: responseStatus.errors
            } as IRequestState
        }
    });

    return newState;
}

const InstitutionContractReducer: Reducer<IInstitutionContractState> = (state: IInstitutionContractState = initialState, action: InstitutionContractAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONCONTRACT_LOAD_BY_INSTITUTION_ID:
        case ACTION_INSTITUTIONCONTRACT_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONCONTRACT_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONCONTRACT_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONCONTRACT_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONCONTRACT_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONCONTRACT_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONCONTRACT_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_FILTER:
            return filter(state, action.formFilter);
        case ACTION_INSTITUTIONCONTRACT_SYNC:
            return loading(state, true);
        case ACTION_INSTITUTIONCONTRACT_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONCONTRACT_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default InstitutionContractReducer;